import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { CAlert, CCard, CCardBody, CSpinner } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilBook } from "@coreui/icons";
import {
  CommunicationCategory,
  CommunicationChannel,
  useCommunicationPreferences,
} from "../../api/hooks/useCommunicationPreferences";
import { useFollowedStrategies } from "../../api/hooks/useFollowedStrategies";
import { helpArticlePath, routePaths } from "../../routes/route-paths";
import { FollowTable } from "./components/FollowTable";
import { StrategyMarksKey } from "./components/StrategyMarksKey";
import { useFollowableStrategies } from "./useFollowableStrategies";

/** What the customer follows, read-only. Changing it is the Change what you follow workflow. */
export function ConfigureSignals() {
  const { t } = useTranslation("signals");
  const followable = useFollowableStrategies();
  const followed = useFollowedStrategies();
  const preferences = useCommunicationPreferences();

  const isLoading = followable.isLoading || followed.isLoading;
  const loadError = followable.error ?? followed.error;

  const followedIds = new Set(followed.data ?? []);
  const following = followable.strategies.filter((strategy) => followedIds.has(strategy.id));

  const signalEmail = preferences.data?.find(
    (p) => p.category === CommunicationCategory.Signals && p.channel === CommunicationChannel.Email,
  );

  return (
    <div>
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-start gap-2 mb-4">
        <div>
          <h1 className="mb-1">{t("configure.title")}</h1>
          {!isLoading && !loadError && followable.strategies.length > 0 && (
            <p className="text-body-secondary mb-0">
              {t("configure.summary", { following: following.length, total: followable.strategies.length })}
            </p>
          )}
        </div>
        {/* Full width on a phone, where they otherwise sit small under the heading. */}
        <div className="col-12 col-sm-auto d-flex flex-column flex-sm-row gap-2">
          <Link to={helpArticlePath("reading-your-signals")} className="btn btn-outline-secondary">
            <CIcon icon={cilBook} className="me-2" />
            {t("page.help")}
          </Link>
          {!isLoading && !loadError && followable.strategies.length > 0 && (
            <Link to={routePaths.signalsConfigureChange} className="btn btn-primary">
              {t("configure.change")}
            </Link>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="d-flex justify-content-center py-5">
          <CSpinner color="primary" />
        </div>
      )}

      {!isLoading && loadError && <CAlert color="danger">{t("configure.loadError")}</CAlert>}

      {!isLoading && !loadError && followable.strategies.length === 0 && (
        <CCard>
          <CCardBody className="text-center py-5">
            <h2 className="h5 mb-2">{t("empty.noStrategyTitle")}</h2>
            <p className="text-body-secondary mx-auto mb-4" style={{ maxWidth: 480 }}>
              {t("empty.noStrategyBody")}
            </p>
            <Link to={routePaths.strategies} className="btn btn-primary">
              {t("empty.browseStrategies")}
            </Link>
          </CCardBody>
        </CCard>
      )}

      {!isLoading && !loadError && followable.strategies.length > 0 && (
        <>
          <CCard className="mb-3">
            <CCardBody className="d-flex flex-column gap-3">
              {following.length > 0 ? (
                <>
                  <FollowTable strategies={following} />
                  <StrategyMarksKey strategies={following} />
                </>
              ) : (
                <p className="text-body-secondary mb-0">{t("configure.noneFollowed")}</p>
              )}
            </CCardBody>
          </CCard>

          {signalEmail && (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 small text-body-secondary">
              <span>
                <Trans
                  t={t}
                  i18nKey={signalEmail.allowed ? "configure.emailsOn" : "configure.emailsOff"}
                  components={{ strong: <strong /> }}
                />
              </span>
              <Link to={routePaths.profileCommunications} className="btn btn-outline-primary btn-sm">
                {t("configure.changeEmails")}
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ConfigureSignals;
